import { useEffect, useState } from "react";
import { Share2, Eye, EyeOff, Plug, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function SocialConfigForm({ businessId }) {
  const [cfg, setCfg] = useState({ configured: false, fb_page_id: "", ig_account_id: "", token_masked: "" });
  const [token, setToken] = useState("");
  const [showToken, setShowToken] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setCfg({ configured: false, fb_page_id: "", ig_account_id: "", token_masked: "" });
    setToken("");
    setShowToken(false); setLoaded(false); setError("");
    if (!businessId) return;
    let active = true;
    api.get(`/businesses/${businessId}/meta-config`)
      .then((r) => { if (active) { setCfg(r.data); setLoaded(true); } })
      .catch((e) => { if (active) setError(apiError(e)); });
    return () => { active = false; };
  }, [businessId]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/businesses/${businessId}/meta-config`, {
        fb_page_access_token: token.trim(),
        fb_page_id: cfg.fb_page_id,
        ig_account_id: cfg.ig_account_id,
      });
      setCfg(r.data);
      setToken("");
      toast.success("Konfigurasi Meta bisnis disimpan (token terenkripsi)");
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const test = async () => {
    setTesting(true);
    try {
      const r = await api.post(`/businesses/${businessId}/meta-test`, {
        fb_page_access_token: token.trim(),
        fb_page_id: cfg.fb_page_id,
        ig_account_id: cfg.ig_account_id,
      });
      if (r.data.ok) toast.success(r.data.message);
      else toast.error(r.data.message);
    } catch (e) { toast.error(apiError(e)); }
    finally { setTesting(false); }
  };

  const clear = async () => {
    if (!window.confirm("Hapus token Meta tersimpan untuk bisnis ini?")) return;
    try {
      const r = await api.put(`/businesses/${businessId}/meta-config`, { clear_token: true, fb_page_id: cfg.fb_page_id, ig_account_id: cfg.ig_account_id });
      setCfg(r.data);
      setToken("");
      toast.success("Token Meta bisnis dihapus");
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="bg-[#FFFFFF] border border-[#E2E8F0] rounded-2xl p-4" data-testid="meta-config-card">
      <div className="flex items-center gap-2 mb-1">
        <Share2 size={16} className="text-[#2563EB]" />
        <h3 className="font-heading font-bold text-sm">Integrasi Sosial Media</h3>
      </div>
      <p className="text-[11px] text-slate-500 mb-3">
        Facebook Page &amp; Instagram Business untuk posting langsung dan terjadwal bisnis ini.
        Status token: {cfg.token_masked
          ? <span className="text-[#059669] font-semibold">tersimpan <span className="font-mono-code text-slate-600">({cfg.token_masked})</span></span>
          : <span className="text-slate-600">belum diatur</span>}
      </p>
      {cfg.token_unavailable && <p role="alert" className="text-xs text-amber-600 mb-2">Token lama tidak dapat dibaca; isi ulang atau hapus token.</p>}
      {!businessId && <p className="text-xs text-slate-600 mb-2">Simpan bisnis terlebih dahulu untuk mengatur integrasi sosial media.</p>}
      {error && <p role="alert" className="text-xs text-red-600 mb-2">{error}</p>}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="md:col-span-2">
          <Label className="text-xs text-slate-600">Facebook Page Access Token</Label>
          <div className="relative mt-1">
            <Input
              type={showToken ? "text" : "password"}
              value={token}
              onChange={(e) => setToken(e.target.value)}
              data-testid="meta-token-input"
              className="bg-[#F1F5F9] border-[#E2E8F0] pr-10 font-mono-code text-xs"
              placeholder={cfg.token_masked ? `${cfg.token_masked} (tersimpan)` : "EAA... — token dari Meta for Developers"}
              autoComplete="off"
            />
            <button type="button" onClick={() => setShowToken((v) => !v)} data-testid="meta-token-toggle" aria-label={showToken ? "Sembunyikan token Meta" : "Tampilkan token Meta"} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-800 transition-colors">
              {showToken ? <EyeOff size={15} /> : <Eye size={15} />}
            </button>
          </div>
          <p className="mt-1 text-[11px] text-slate-500">Token harus mewakili Facebook Page ID di bawah. Jika memakai User Token yang memiliki akses pengelolaan Page, aplikasi akan mengambil Page Token yang sesuai untuk posting Facebook.</p>
        </div>
        <div className="flex min-w-0 flex-col gap-1">
          <Label className="text-xs leading-5 text-slate-600">Facebook Page ID</Label>
          <Input value={cfg.fb_page_id} onChange={(e) => setCfg({ ...cfg, fb_page_id: e.target.value })} data-testid="meta-page-id-input" className="bg-[#F1F5F9] border-[#E2E8F0] mt-auto font-mono-code text-xs" placeholder="123456789012345" />
        </div>
        <div className="flex min-w-0 flex-col gap-1">
          <Label className="text-xs leading-5 text-slate-600">Instagram Business Account ID</Label>
          <Input value={cfg.ig_account_id} onChange={(e) => setCfg({ ...cfg, ig_account_id: e.target.value })} data-testid="meta-ig-id-input" className="bg-[#F1F5F9] border-[#E2E8F0] mt-auto font-mono-code text-xs" placeholder="17841400000000000" />
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mt-3">
        <Button type="button" onClick={test} disabled={testing || saving || !loaded || !businessId || !cfg.fb_page_id.trim()} variant="outline" data-testid="meta-test-button" className="border-[#3B82F6]/40 bg-[#3B82F6]/10 hover:bg-[#3B82F6]/25 text-[#2563EB] rounded-xl text-xs">
          {testing ? <Loader2 size={14} className="animate-spin mr-1" /> : <Plug size={14} className="mr-1" />}
          Uji Koneksi
        </Button>
        <Button type="button" onClick={save} disabled={saving || !loaded || !businessId} data-testid="meta-save-button" className="bg-[#2563EB] hover:bg-[#1D4ED8] rounded-xl text-xs">
          {saving ? <Loader2 size={14} className="animate-spin mr-1" /> : null} Simpan Konfigurasi
        </Button>
        {(cfg.token_masked || cfg.token_unavailable) && (
          <Button type="button" onClick={clear} variant="outline" data-testid="meta-clear-button" className="border-[#E11D48]/40 bg-[#E11D48]/10 hover:bg-[#E11D48]/25 text-[#E11D48] rounded-xl text-xs">
            Hapus Token
          </Button>
        )}
      </div>
      <p className="text-[10px] text-slate-500 mt-2">Butuh permission Meta: pages_manage_posts, pages_show_list, instagram_basic, instagram_content_publish. Gambar Instagram diambil dari Galeri Media (URL publik).</p>
    </div>
  );
}
